import React, { useState } from 'react';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';

import {
    Box,
    Button,
    Grid,
    IconButton,
    makeStyles,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from '@material-ui/core';

import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import { excluiHorariosDoDia, hoursOrderByWeekDay, onClearBoard } from '../ServicesForms/Lines/utils/filtros';
import PopoverMenuTable from './PopoverMenuTable';
import { setSchedulesStore } from 'app/store/slices/line';
import notification from '../../config/notification';
import { SCHEDULES_TYPES } from '../../config/services';
import { TIMEZONE_OFFSET } from '../../utils/constants';
import uuidUtil from '../../utils/uuid';

const DAYS = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sab', 'Dom'];

const ScheduleTableBoard = (props) => {
    const dispatch = useDispatch();
    const classes = useStyles();
    const { readOnly, selectedScheduleTypes } = props;
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [route, setRoute] = useState();

    const open = Boolean(anchorEl);

    const schedulesStore = useSelector((state) => state.line.schedulesStore);
    const isStrip = useSelector((state) => state.line.isStrip);

    const onOpenPopover = (e, dayIndex, route) => {
        setSelectedIndex(dayIndex);
        setRoute(route);
        setAnchorEl(e.currentTarget);
    };

    const handleClosePopover = () => {
        setAnchorEl(null);
    };

    const findHourWithoutDefinedId = (hourList, { deDiaSemana, deSentido, hrHorario }) => {
        return hourList.find((hour) => {
            return (
                hour.deDiaSemana === deDiaSemana &&
                hour.deSentido === deSentido &&
                moment(hrHorario).format('HH:mm') === moment(hour.hrHorario).format('HH:mm')
            );
        });
    };

    const handleAddSchedule = (hourListToSaveOnStore) => {
        const onlyNewHours = hourListToSaveOnStore.filter((item) => {
            return !findHourWithoutDefinedId(schedulesStore, item);
        });

        const filteredSchedules = schedulesStore.map((entry) => {
            const found = hourListToSaveOnStore.find((hour) => {
                if (entry.cdLinhaQuadroHorarioHora) {
                    return hour.cdLinhaQuadroHorarioHora === entry.cdLinhaQuadroHorarioHora;
                }
                return entry.tempId && entry.tempId === hour.tempId;
            });
            if (found) return found;
            return entry;
        });

        dispatch(setSchedulesStore([...filteredSchedules, ...onlyNewHours]));
        notification('Horário alterado com sucesso', { type: 'success' });
        return true;
    };

    const handleCopyColumn = ({ day, selectedTargets }) => {
        if (selectedTargets.length <= 0) return;
        const newSchedulesData = [];
        const originSchedules = schedulesStore.filter((entry) => {
            return (
                entry.deDiaSemana.toUpperCase() === day.toUpperCase() &&
                entry.deSentido.toUpperCase() === route.toUpperCase()
            );
        });
        let filteredSchedules = [...schedulesStore];
        selectedTargets.forEach((targetIdx) => {
            filteredSchedules = excluiHorariosDoDia(filteredSchedules, DAYS[targetIdx], route);
            originSchedules.forEach((prevOriginSchedule) => {
                const index = filteredSchedules.findIndex(
                    (time) =>
                        time.deDiaSemana.toUpperCase() === DAYS[targetIdx].toUpperCase() &&
                        time.hrHorario === prevOriginSchedule.hrHorario &&
                        time.deSentido.toUpperCase() === route.toUpperCase() &&
                        !prevOriginSchedule.boExcluido,
                );
                if (index >= 0) {
                    filteredSchedules[index].boExcluido = false;
                    filteredSchedules[index].isNew = false;
                    return;
                }
                const tempId = uuidUtil.generate();
                newSchedulesData.push({
                    ...prevOriginSchedule,
                    tempId: tempId,
                    permissionarios: copiarPermissionario(prevOriginSchedule, tempId),
                    cdLinhaQuadroHorarioHora: null,
                    deDiaSemana: DAYS[targetIdx].toUpperCase(),
                    deSentido: route.toUpperCase(),
                    boExcluido: prevOriginSchedule.boExcluido ? prevOriginSchedule.boExcluido : false,
                    isNew: true,
                });
            });
        });

        dispatch(setSchedulesStore([...filteredSchedules, ...newSchedulesData]));
    };

    const copiarPermissionario = (prevOriginSchedule, tempId) => {
        if (!prevOriginSchedule.permissionarios) return [];
        return prevOriginSchedule.permissionarios.map((p) => {
            return { ...p, cdLinhaQuadroHorarioHora: null, cdLinhaQuadroHoraResponsavel: null, tempId: tempId };
        });
    };

    const handleClearColumn = (day) => {
        if (window.confirm('Deseja realmente limpar essa coluna?')) {
            const filteredSchedules = excluiHorariosDoDia(schedulesStore, day, route);
            dispatch(setSchedulesStore(filteredSchedules));
        }
    };

    const handleClearBoard = () => {
        if (window.confirm('Deseja realmente limpar esse quadro de horário?')) {
            dispatch(setSchedulesStore(onClearBoard(schedulesStore)));
        }
    };

    const fixTimezone = (data) => {
        const currentOffset = moment().utcOffset();
        const offset = data?.isNew ? currentOffset : TIMEZONE_OFFSET.AMERICA_SAOPAULO;
        return moment(data.hrHorario).utcOffset(offset);
    };

    const renderTableCells = (times, i) => {
        return DAYS.map((c, j) => {
            const hour = times[j][i];
            if (hour) {
                return (
                    <TableCell key={`${hour.hrHorario}-${i}-${j}`} align="center">
                        {fixTimezone(hour).format('HH:mm')}
                    </TableCell>
                );
            } else {
                return (
                    <TableCell key={`cel-${i}-${j}`} align="center">
                        -
                    </TableCell>
                );
            }
        });
    };

    function getTableBody(route) {
        const validSchedules = schedulesStore.filter((entry) => !entry.boExcluido);
        const { times: timesIda } = hoursOrderByWeekDay(validSchedules || [], 'IDA');
        const { times: timesVolta } = hoursOrderByWeekDay(validSchedules || [], 'VOLTA');

        let totalRows = 0;
        [...timesIda, ...timesVolta].forEach((hrs) => {
            const count = hrs.length;
            if (count > totalRows) totalRows = count;
        });

        if (isStrip) {
            if (SCHEDULES_TYPES.ZERADO === Number(selectedScheduleTypes)) {
                return (
                    <TableRow>
                        <TableCell colSpan={7} className={classes.scheduleRowType}>
                            QUADRO DE HORÁRIO ZERADO
                        </TableCell>
                    </TableRow>
                );
            } else if (SCHEDULES_TYPES.CIRCULAR === Number(selectedScheduleTypes) && route === 'volta') {
                return [...Array(totalRows)].map((r, i) => (
                    <TableRow key={i}>
                        <TableCell colSpan={7} className={classes.scheduleRowType}>
                            CIRCULAR
                        </TableCell>
                    </TableRow>
                ));
            }
        }

        return (
            <TableBody>
                {[...Array(totalRows)].map((r, i) => (
                    <TableRow key={i}>
                        {renderTableCells(route.toUpperCase() === 'IDA' ? timesIda : timesVolta, i)}
                    </TableRow>
                ))}
            </TableBody>
        );
    }

    const prepareTable = (route) => {
        return (
            <TableContainer>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            {DAYS.map((day, index) => (
                                <TableCell
                                    style={{
                                        backgroundColor: route === 'ida' ? '#1d7ccc' : '#246aa6',
                                        color: '#fff',
                                        padding: '6px',
                                    }}
                                    key={`head${index}`}>
                                    <Box style={{ display: 'flex', justifyContent: 'center' }}>
                                        {day.toLowerCase() === 'sab' ? 'Sáb' : day}
                                        {!readOnly && (
                                            <IconButton
                                                color="inherit"
                                                size="small"
                                                onClick={(event) => onOpenPopover(event, index, route)}>
                                                <ArrowDropDownIcon fontSize="inherit" />
                                            </IconButton>
                                        )}
                                    </Box>
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    {getTableBody(route)}
                </Table>
            </TableContainer>
        );
    };

    return (
        <>
            <Grid item xs={12} md={6} container>
                <Grid item xs={12}>
                    <h3 className={classes.titleTable}>IDA</h3>
                </Grid>
                <Grid item xs={12} container style={{ overflow: 'hidden' }}>
                    {prepareTable('ida')}
                </Grid>
            </Grid>
            <Grid item xs={12} md={6} container>
                <Grid item xs={12}>
                    <h3 className={classes.titleTable}>VOLTA</h3>
                </Grid>
                <Grid item xs={12} container style={{ overflow: 'hidden' }}>
                    {prepareTable('volta')}
                </Grid>
            </Grid>
            {!readOnly && (
                <Grid item xs={12} container justifyContent="flex-end" style={{ marginTop: '25px' }}>
                    <Button
                        variant="outlined"
                        color="primary"
                        disableElevation
                        className={classes.addButton}
                        onClick={() => handleClearBoard()}>
                        Limpar Quadro de Horários
                    </Button>
                </Grid>
            )}
            <PopoverMenuTable
                open={open}
                handleClose={handleClosePopover}
                anchorEl={anchorEl}
                setAnchorEl={setAnchorEl}
                classes={classes}
                onAddSchedule={handleAddSchedule}
                onCopyColumn={handleCopyColumn}
                onClearColumn={handleClearColumn}
                dayIndex={selectedIndex}
                day={DAYS[selectedIndex].toUpperCase()}
                permissionaries={props.permissionaries}
                route={route}
            />
        </>
    );
};

const useStyles = makeStyles((theme) => ({
    gridHeader: {
        backgroundColor: (props) =>
            props.route?.toLowerCase() === 'ida' ? theme.palette.primary.main : theme.palette.primary.dark,
        padding: theme.spacing(1),
        color: theme.palette.common.white,
        fontWeight: '500',
        display: 'flex',
        justifyContent: 'center',
        lineHeight: theme.spacing(0.2),
        textAlign: 'center',
    },
    gridItem: {
        padding: theme.spacing(1),
        lineHeight: theme.spacing(0.2),
        minWidth: '63px',
        textAlign: 'center',
    },
    addButton: {
        borderRadius: '25px',
        // color: theme.palette.primary.contrastText,
    },
    titleTable: {
        fontWeight: '500',
    },
    scheduleRowType: {
        textAlign: 'center',
        fontWeight: 'bold',
    },
}));

export default ScheduleTableBoard;
